/**
 * Utilitários para formatação e manipulação de peso
 */

/**
 * Formata um valor de peso para exibição com separadores de milhares e a unidade "KG"
 * @param value - Valor a ser formatado (string com números e vírgula opcional)
 * @returns Valor formatado com separadores de milhares e "KG"
 *
 * @example
 * formatWeight('1000') // "1.000 KG"
 * formatWeight('1500,5') // "1.500,5 KG"
 * formatWeight('abc123') // "123 KG"
 */
export const formatWeight = (value: string, suffix: string = 'KG'): string => {
  const clean = value.replace(/[^0-9,]/g, '')
  if (!clean) return ''

  if (clean.includes(',')) {
    const [inteiro, decimal] = clean.split(',')
    const numeroFormatado = parseInt(inteiro || '0').toLocaleString('pt-BR')
    return `${numeroFormatado},${decimal} ${suffix}`
  }

  const numeroFormatado = parseInt(clean).toLocaleString('pt-BR')
  return `${numeroFormatado} ${suffix}`
}

/**
 * Extrai o valor numérico de um peso formatado para exibição
 * @param displayValue - Valor formatado com "KG" e separadores
 * @returns Valor numérico do peso
 *
 * @example
 * getNumericValue('1.500,5 KG') // 1500.5
 * getNumericValue('1.000 KG') // 1000
 * getNumericValue('500KG') // 500
 */
export const getNumericValue = (displayValue: string): number => {
  const clean = displayValue.replace(/\s*KG\s*/gi, '').trim()

  if (clean.includes(',')) {
    const number = parseFloat(clean.replace(/\./g, '').replace(',', '.'))
    return number || 0
  }

  const number = parseInt(clean.replace(/\./g, ''))
  return number || 0
}

export const cleanWeightValue = (value: string): string => {
  return value.replace(/[^0-9,]/g, '')
}

/**
 * Converte um peso em string para número, lidando com formatação brasileira
 * @param weight - Peso como string (pode conter vírgula decimal)
 * @returns Peso como número
 *
 * @example
 * parseWeightToNumber('150,5') // 150.5
 * parseWeightToNumber('1000') // 1000
 */
export const parseWeightToNumber = (weight: string): number => {
  if (!weight) return 0

  const cleanWeight = weight.replace(/[^\d,]/g, '')
  if (cleanWeight.includes(',')) {
    return parseFloat(cleanWeight.replace(',', '.'))
  }

  return parseInt(cleanWeight) || 0
}

/**
 * Valida se um valor de peso é válido
 * @param weight - Valor do peso a ser validado
 * @returns true se o peso é válido, false caso contrário
 *
 * @example
 * isValidWeight('150,5') // true
 * isValidWeight('abc') // false
 * isValidWeight('') // false
 */
export const isValidWeight = (weight: string): boolean => {
  const numericValue = getNumericValue(weight)
  return numericValue > 0 && numericValue <= 99999 // Limite máximo razoável para peso de animal
}
